import { Link } from "react-router-dom";
import { hotList, trendMeta } from "@/mocks/hot";
import { videos } from "@/mocks/videos";
import { categories } from "@/mocks/categories";

function SectionHead({
  kicker,
  title,
  desc,
  link,
  linkLabel,
}: {
  kicker: string;
  title: string;
  desc: string;
  link?: string;
  linkLabel?: string;
}) {
  return (
    <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-accent-600">{kicker}</p>
        <h2 className="mt-1 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{title}</h2>
        <p className="mt-2 max-w-2xl text-sm text-foreground-600">{desc}</p>
      </div>
      {link && (
        <Link to={link} className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700">
          {linkLabel} <i className="ri-arrow-right-line" />
        </Link>
      )}
    </div>
  );
}

export default function HotRanking() {
  const rankVideos = hotList.map((h) => videos.find((v) => v.id === h.videoId)!).filter(Boolean);
  const first = rankVideos[0];
  const rest = rankVideos.slice(1);

  return (
    <section className="mx-auto max-w-7xl px-4 py-14 md:px-6">
      <SectionHead
        kicker="Hot Now"
        title="热门吃瓜榜"
        desc="基于全网热度与讨论度实时排序，看看今天大家都在吃谁的瓜。"
      />
      <div className="grid gap-6 lg:grid-cols-12">
        {/* Featured */}
        {first && (
          <article className="group relative lg:col-span-5">
            <Link to={`/videos/${first.id}`} className="block overflow-hidden rounded-2xl">
              <div className="relative aspect-[4/3] w-full overflow-hidden">
                <img
                  src={first.cover}
                  alt={`${first.title} 视频封面`}
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/10 to-transparent"></div>
                <span className="absolute left-4 top-4 flex items-center gap-1 rounded-full bg-primary-500 px-3 py-1 text-xs font-bold text-background-50">
                  <i className="ri-fire-fill" /> 热榜第一
                </span>
                <span className="absolute bottom-2 right-3 rounded bg-foreground-950/70 px-1.5 py-0.5 text-xs text-background-50">
                  {first.duration}
                </span>
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <p className="text-xs font-medium text-accent-300">
                    {categories.find((c) => c.id === first.categoryId)?.name ?? "资讯"}
                  </p>
                  <h3 className="mt-1 font-heading text-xl font-bold leading-snug text-background-50 md:text-2xl">
                    {first.title}
                  </h3>
                  <p className="mt-2 flex items-center gap-4 text-xs text-background-100/80">
                    <span className="flex items-center gap-1"><i className="ri-eye-line" /> {first.views}</span>
                    <span className="flex items-center gap-1"><i className="ri-chat-3-line" /> {first.comments.toLocaleString()}</span>
                  </p>
                </div>
              </div>
            </Link>
          </article>
        )}

        {/* Rank list */}
        <div className="lg:col-span-7">
          <ol className="space-y-3">
            {rest.map((v) => {
              const item = hotList.find((h) => h.videoId === v.id)!;
              const meta = trendMeta[item.trend];
              const top3 = item.rank <= 3;
              return (
                <li key={v.id}>
                  <Link
                    to={`/videos/${v.id}`}
                    className="flex items-center gap-4 rounded-xl bg-background-50 px-4 py-3 ring-1 ring-inset ring-background-200 transition hover:bg-background-100 hover:ring-primary-300"
                  >
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg font-heading text-base font-bold ${
                        top3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-600"
                      }`}
                    >
                      {item.rank}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-foreground-900">{v.title}</span>
                      <span className="mt-0.5 block truncate text-xs text-foreground-500">{v.summary}</span>
                    </span>
                    <span className="hidden shrink-0 items-center gap-1 text-xs text-foreground-500 sm:flex">
                      <i className="ri-fire-line text-primary-500" /> {item.heat}
                    </span>
                    <span className="flex shrink-0 items-center gap-1 rounded-full bg-background-100 px-2 py-1 text-xs text-foreground-600">
                      <i className={meta.icon} /> {meta.label}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}